import { useEffect } from 'react';

import {
  type ConnectionErrorForm,
  failConnectionAddOperation,
  failConnectionReconnectOperation,
  startConnectionAddOperation,
  startConnectionReconnectOperation,
  useConnectionApiErrorHandler,
} from '@mint-lib/connections-ui';
import type {
  AuthorizeResult,
  NormalizedConnection,
  OAuthFlow,
} from '@mint-lib/integration-registry';
import { OMNI_URN } from '@mint-lib/omni-integration';
import { OmniAuthAuthorize } from '@mint-sdk/api-omni';

import { type AmazonRegion, isAmazonRegion } from './regions.js';

/**
 * Ported from `packages/connections/src/components/_organisms/ManageConnectionForm/traffickingReconnectStrategies.ts`'s
 * `amazonAuthorizeStrategy` — the one quirk: authorize needs a `region`
 * (threaded from the add form's `region` field / the manage form's stored
 * `amazonRegion`), routed to the Amazon-specific mutation instead of the
 * generic one @mint-lib/omni-integration uses.
 */
export interface AmazonAuthorizeParams {
  connection?: NormalizedConnection;
  connectionName?: string;
  /** Defaults to the region stored on `connection` (`extra.amazonRegion`), for reconnects. */
  region?: AmazonRegion | null;
  /** Receives field errors (duplicate name, missing region); without it every error is a snackbar. */
  form?: ConnectionErrorForm;
}

const storedRegion = (
  connection?: NormalizedConnection,
): AmazonRegion | null => {
  const value = connection?.extra?.amazonRegion;
  return isAmazonRegion(value) ? value : null;
};

export function useAmazonAuthorize({
  connection,
  connectionName,
  region,
  form,
}: AmazonAuthorizeParams): AuthorizeResult {
  const { mutate, data, isPending } =
    OmniAuthAuthorize.omniAuthAmazonAuthorizeV2();
  const handleApiError = useConnectionApiErrorHandler({ form });

  const redirectUrl = data?.data.items[0]?.redirectUrl;

  useEffect(() => {
    if (redirectUrl) {
      globalThis.location.assign(redirectUrl);
    }
  }, [redirectUrl]);

  return {
    authorize: () => {
      // Without a connection this is the add form; with one, a reconnect.
      const context = {
        platform: OMNI_URN.amazon,
        flow: 'oauth',
        registry: true,
      } as const;
      if (connection) {
        startConnectionReconnectOperation(context);
      } else {
        startConnectionAddOperation(context);
      }
      mutate(
        {
          data: {
            connectionId: connection?.id,
            connectionName: connectionName ?? connection?.name ?? '',
            region: region ?? storedRegion(connection),
          },
        },
        {
          onError: (error) => {
            failConnectionAddOperation('error');
            failConnectionReconnectOperation('error');
            handleApiError(error);
          },
        },
      );
    },
    isPending,
  };
}

export const amazonOAuthFlow: OAuthFlow = {
  canProcess: () => false,
  useAuthorize: ({ connection }) => useAmazonAuthorize({ connection }),
};
